<template>
  <div class="model-viewer-container">
    <!-- 3D渲染容器 -->
    <div ref="modelContainer" class="model-container"></div>
    <!-- 加载状态提示 -->
    <div class="loading" v-if="loading">
      加载中：{{ loadProgress }}%
    </div>
    <!-- 错误提示 -->
    <div class="error" v-if="errorMsg">
      {{ errorMsg }}
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { MTLLoader } from 'three/addons/loaders/MTLLoader.js';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

export default {
  props: {
    modelUrl: {
      type: String,
      required: true
    },
    scale: {
      type: Number,
      default: null
    },
    rotation: {
      type: Array,
      default: () => [0, 0, 0]
    }
  },

  data() {
    return {
      loading: true,
      loadProgress: 0,
      errorMsg: '',
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      animateId: null,
      model: null,
      format: null
    };
  },

  mounted() {
    if (this.$refs.modelContainer) {
      this.detectFormat();
      this.initScene();
      // 监听窗口resize（关键：动态适配尺寸）
      window.addEventListener('resize', this.handleResize);
    } else {
      this.loading = false;
      this.errorMsg = '模型容器未找到';
    }
  },

  beforeDestroy() {
    // 清理资源，避免内存泄漏
    window.removeEventListener('resize', this.handleResize);
    this.cleanupScene();
  },

  methods: {
    detectFormat() {
      const ext = this.modelUrl.split('.').pop().toLowerCase();
      if (ext === 'obj') {
        this.format = 'obj';
      } else if (ext === 'fbx') {
        this.format = 'fbx';
      } else if (['gltf', 'glb'].includes(ext)) {
        this.format = 'gltf';
      } else {
        this.loading = false;
        this.errorMsg = `不支持的模型格式：.${ext}`;
      }
    },

    initScene() {
      try {
        // 创建场景
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x080a12);

        // 强制获取容器尺寸（关键：解决初始尺寸为0的问题）
        const container = this.$refs.modelContainer;
        const width = container.clientWidth || 1156; // 兜底宽度
        const height = container.clientHeight || 500; // 兜底高度

        // 创建相机
        this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        this.camera.position.set(0, 5, 15);

        // 创建渲染器（强制设置尺寸）
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(width, height);
        container.appendChild(this.renderer.domElement);

        // 添加光照
        this.addLights();

        // 加载模型
        this.loadModelByFormat();

        // 初始化控制器
        this.initControls();

        // 启动动画循环
        this.animate();
      } catch (err) {
        this.loading = false;
        this.errorMsg = `初始化失败：${err.message}`;
        console.error(err);
      }
    },

    addLights() {
      // 环境光（基础照明）
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
      this.scene.add(ambientLight);

      // 方向光（增强立体感）
      const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.6);
      directionalLight1.position.set(10, 20, 10);
      this.scene.add(directionalLight1);

      const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
      directionalLight2.position.set(-10, 10, -10);
      this.scene.add(directionalLight2);
    },

    initControls() {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableRotate = true;
      this.controls.rotateSpeed = 1.0;
      this.controls.enableZoom = true;
      this.controls.zoomSpeed = 0.8;
      this.controls.enablePan = true;
      this.controls.panSpeed = 0.5;
      this.controls.target.set(0, 2, 0);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
    },

    loadModelByFormat() {
      switch (this.format) {
        case 'obj':
          this.loadOBJModel();
          break;
        case 'fbx':
          this.loadFBXModel();
          break;
        case 'gltf':
          this.loadGLTFModel();
          break;
      }
    },

    loadOBJModel() {
      const mtlLoader = new MTLLoader();
      const mtlUrl = this.modelUrl.replace('.obj', '.mtl');

      mtlLoader.load(
        mtlUrl,
        (materials) => {
          materials.preload();
          const objLoader = new OBJLoader();
          objLoader.setMaterials(materials);

          objLoader.load(
            this.modelUrl,
            (object) => this.processLoadedModel(object),
            (xhr) => this.updateProgress(xhr),
            (error) => this.handleLoadError(error)
          );
        },
        (xhr) => this.updateProgress(xhr),
        (error) => {
          console.warn('材质加载失败，使用默认材质:', error);
          const objLoader = new OBJLoader();
          objLoader.load(
            this.modelUrl,
            (object) => this.processLoadedModel(object),
            (xhr) => this.updateProgress(xhr),
            (err) => this.handleLoadError(err)
          );
        }
      );
    },

    loadFBXModel() {
      const loader = new FBXLoader();
      loader.load(
        this.modelUrl,
        (object) => this.processLoadedModel(object),
        (xhr) => this.updateProgress(xhr),
        (error) => this.handleLoadError(error)
      );
    },

    loadGLTFModel() {
      const loader = new GLTFLoader();
      loader.load(
        this.modelUrl,
        (gltf) => this.processLoadedModel(gltf.scene),
        (xhr) => this.updateProgress(xhr),
        (error) => this.handleLoadError(error)
      );
    },

    processLoadedModel(model) {
      this.model = model;
      // 应用缩放
      const defaultScale = { obj: 0.01, fbx: 0.01, gltf: 1.0 };
      const scale = this.scale !== null ? this.scale : defaultScale[this.format];
      model.scale.set(scale, scale, scale);

      // 应用旋转
      model.rotation.set(...this.rotation);

      // 居中模型
      this.centerModel(model);

      this.scene.add(model);
      this.loading = false;
      // 通知父组件加载完成
      this.$emit('load');
    },

    centerModel(model) {
      const box = new THREE.Box3().setFromObject(model);
      const center = new THREE.Vector3();
      box.getCenter(center);
      model.position.sub(center);
    },

    updateProgress(xhr) {
      this.loadProgress = Math.round((xhr.loaded / xhr.total) * 100);
    },

    handleLoadError(error) {
      this.loading = false;
      this.errorMsg = `模型加载失败：${error.message}`;
      console.error(error);
    },

    animate() {
      this.animateId = requestAnimationFrame(this.animate);
      this.controls?.update();
      this.renderer?.render(this.scene, this.camera);
    },

    // 关键：窗口resize时更新尺寸
    handleResize() {
      const container = this.$refs.modelContainer;
      if (container && this.renderer && this.camera) {
        const width = container.clientWidth;
        const height = container.clientHeight;
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
      }
    },

    cleanupScene() {
      if (this.animateId) cancelAnimationFrame(this.animateId);
      if (this.model && this.scene) this.scene.remove(this.model);
      if (this.renderer?.domElement && this.$refs.modelContainer) {
        this.$refs.modelContainer.removeChild(this.renderer.domElement);
      }
      this.controls?.dispose();
      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.controls = null;
    }
  }
};
</script>

<style scoped>
.model-viewer-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.model-container {
  width: 100%;
  height: 100%;
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  color: #333;
  font-size: 14px;
}

.error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 8px 16px;
  background: rgba(255, 90, 90, 0.8);
  border-radius: 4px;
  color: white;
  font-size: 14px;
  max-width: 80%;
  text-align: center;
}
</style>